/**
 * AB#21984 — search as you type on the dashboard.
 *
 * `GET dashboard?suggest_for=text` answers up to six matching orders plus the
 * companies and customers named on them, inside the person's own scope (it is
 * the dashboard's listing query). Asked after a short pause in typing, never
 * for a single character; an answer that arrives after a newer question is
 * dropped. Recent searches are a per-browser convenience kept in localStorage.
 */
import { ref } from 'vue';
import { http } from '@/interceptors/axiosRequestInterceptor';

export interface SuggestOrder {
  order_item_id: number;
  order_id: number;
  order_no: string | null;
  company_name: string | null;
  customer_name: string | null;
  factory_name: string | null;
  status: string | null;
}
export interface Suggestions {
  q: string;
  orders: SuggestOrder[];
  companies: string[];
  customers: string[];
}

export const SUGGEST_MIN_CHARS = 2;
export const SUGGEST_DELAY_MS = 280;
export const RECENT_KEY = 'dashboard_recent_searches';
export const RECENT_MAX = 6;

export const readRecent = (): string[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(raw)
      ? raw.filter((s) => typeof s === 'string' && s.trim() !== '').slice(0, RECENT_MAX)
      : [];
  } catch {
    return [];
  }
};

/** Newest first, no duplicates (whatever the case), at most RECENT_MAX. */
export const rememberSearch = (text: string): string[] => {
  const t = text.trim();
  if (t.length < SUGGEST_MIN_CHARS) return readRecent();
  const next = [t, ...readRecent().filter((s) => s.toLowerCase() !== t.toLowerCase())].slice(
    0,
    RECENT_MAX
  );
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable: recent searches simply do not persist */
  }
  return next;
};

export const clearRecent = (): void => {
  try {
    localStorage.removeItem(RECENT_KEY);
  } catch {
    /* nothing to clear */
  }
};

const empty = (q = ''): Suggestions => ({ q, orders: [], companies: [], customers: [] });

export function useSearchSuggest(delayMs: number = SUGGEST_DELAY_MS) {
  const suggestions = ref<Suggestions>(empty());
  const loading = ref(false);
  const recent = ref<string[]>(readRecent());
  let timer: ReturnType<typeof setTimeout> | undefined;
  let seq = 0;

  const ask = async (text: string): Promise<void> => {
    const mine = ++seq;
    loading.value = true;
    try {
      const { data } = await http.get('dashboard', { params: { suggest_for: text } });
      if (mine !== seq) return;
      const r = (data?.result ?? {}) as Partial<Suggestions>;
      suggestions.value = {
        q: text,
        orders: Array.isArray(r.orders) ? r.orders : [],
        companies: Array.isArray(r.companies) ? r.companies : [],
        customers: Array.isArray(r.customers) ? r.customers : [],
      };
    } catch {
      // Suggestions are a convenience: a failed one shows nothing, and Enter still searches.
      if (mine === seq) suggestions.value = empty(text);
    } finally {
      if (mine === seq) loading.value = false;
    }
  };

  /** Called on every keystroke. */
  const update = (text: string): void => {
    clearTimeout(timer);
    const t = text.trim();
    if (t.replace(/[#\s]/g, '').length < SUGGEST_MIN_CHARS) {
      seq++;
      loading.value = false;
      suggestions.value = empty(t);
      return;
    }
    timer = setTimeout(() => void ask(t), delayMs);
  };

  const remember = (text: string): void => {
    recent.value = rememberSearch(text);
  };
  const forget = (): void => {
    clearRecent();
    recent.value = [];
  };
  const stop = (): void => {
    clearTimeout(timer);
    seq++;
  };

  return { suggestions, loading, recent, update, remember, forget, stop };
}

/* ------------------------------------------------------------------ ask */

/** ELSA's reading of a sentence: the dashboard's own filters (AB#21984). */
export interface AskResult {
  question: string;
  search: string;
  tab: string | null;
  quick_filters: Record<string, unknown>;
  understood: string[];
  not_understood: string[];
}

/** Three words or more reads as a sentence worth asking ELSA about. */
export const ASK_MIN_WORDS = 3;
export const looksLikeAQuestion = (text: string): boolean =>
  text.trim().split(/\s+/).filter(Boolean).length >= ASK_MIN_WORDS;

/**
 * `POST dashboard { ask }`. Resolves to ELSA's reading, or throws an Error
 * whose message is the sentence to show (the server's own when it sent one).
 */
export const askElsaSearch = async (text: string): Promise<AskResult> => {
  try {
    const { data } = await http.post('dashboard', { ask: text }, { timeout: 45000 });
    const r = (data?.result ?? {}) as Partial<AskResult>;
    return {
      question: text,
      search: typeof r.search === 'string' ? r.search : '',
      tab: typeof r.tab === 'string' ? r.tab : null,
      quick_filters: r.quick_filters && !Array.isArray(r.quick_filters) ? r.quick_filters : {},
      understood: Array.isArray(r.understood) ? r.understood.map(String) : [],
      not_understood: Array.isArray(r.not_understood) ? r.not_understood.map(String) : [],
    };
  } catch (e) {
    const message = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
    throw new Error(
      typeof message === 'string' && message
        ? message
        : 'ELSA could not read that right now. The normal search still works.'
    );
  }
};
